import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const faqs = [
  {
    q: '小草影院需要注册才能观看吗？',
    a: '不需要！小草影院全站资源无需注册，即点即看。我们致力于为您提供最便捷的观影体验，打开网站就能直接观看所有电影、电视剧、综艺和动漫内容。',
  },
  {
    q: '小草影院的视频资源是免费的吗？',
    a: '是的，完全免费！小草影院所有资源均不收费，您可以畅享海量高清影视内容。我们不设置任何付费墙或会员体系，坚持为用户提供永久免费的观影服务。',
  },
  {
    q: '为什么播放时会出现卡顿？',
    a: '小草影院采用先进的CDN加速技术，正常情况下高清视频均可秒加载不卡顿。如果您遇到卡顿，建议检查网络连接状态，或尝试切换网络环境。我们也在持续优化服务器性能，确保流畅体验。',
  },
  {
    q: '小草影院的视频画质如何？',
    a: '我们提供HD高清和4K超清等多种画质选择，大部分热门影片都支持4K超清播放。您可以根据自己的网络情况和设备性能选择最适合的画质，获得最佳观影体验。',
  },
  {
    q: '小草影院支持哪些设备观看？',
    a: '小草影院完美适配电脑、平板、手机等所有主流设备。无论是在家用大屏电脑观看，还是通勤路上用手机追剧，都能获得流畅舒适的观影体验。网站采用响应式设计，自动适配不同屏幕尺寸。',
  },
  {
    q: '新电影上线后多久能在小草影院看到？',
    a: '我们拥有专业的资源更新团队，7×24小时不间断更新。热门新片通常在上线后极短时间内就能在小草影院找到。关注我们的首页"最新更新"栏目，第一时间发现精彩内容。',
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于小草影院',
    description: '小草影院致力于提供免费高清在线观影服务，全站资源不收费、不缓冲秒开播。',
    url: 'https://xiaocaoyingyuan.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '小草影院',
      url: 'https://xiaocaoyingyuan.com/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[{ name: '关于我们' }]} />

            <section className="px-4 md:px-6 py-8 md:py-12" id="about">
              <div className="text-center mb-10">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  关于小草影院
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  小草影院成立于2020年，是一家致力于为全球华人提供免费高清在线观影服务的平台。
                  我们始终坚持"免费、高清、流畅"的核心理念，让每一位用户都能轻松畅享影视盛宴。
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-12">
                {[
                  {
                    icon: 'ri-heart-line',
                    title: '我们的初心',
                    desc: '让每个人都能免费看到好电影，打破付费壁垒，让优质的影视内容触手可及。我们相信，好的内容不应该被价格所限制。',
                  },
                  {
                    icon: 'ri-eye-line',
                    title: '我们的愿景',
                    desc: '成为全球华人首选的免费在线观影平台，用技术和服务为用户创造极致的观影体验，让观影成为一种享受。',
                  },
                  {
                    icon: 'ri-shield-star-line',
                    title: '我们的承诺',
                    desc: '永久免费、高清流畅、安全可靠。我们绝不收取任何费用，持续优化技术架构，为用户守护纯净的观影空间。',
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-6 bg-background-50 border border-background-200/70 rounded-xl text-center"
                  >
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-800 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-8 mb-12">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 text-center">
                  平台数据
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  {[
                    { value: '100,000+', label: '影视资源' },
                    { value: '500万+', label: '月活跃用户' },
                    { value: '99.9%', label: '服务可用率' },
                    { value: '24/7', label: '不间断更新' },
                  ].map((stat) => (
                    <div key={stat.label}>
                      <div className="text-xl md:text-2xl font-heading font-bold text-accent-500">{stat.value}</div>
                      <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100" id="reputation">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500">听听用户们怎么说</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {[
                  {
                    name: '影迷小王',
                    text: '小草影院真的太良心了！资源全免费不说，画质还特别清晰，追剧体验比很多付费平台还好。强烈推荐给身边所有朋友！',
                    rating: 5,
                  },
                  {
                    name: '追剧达人',
                    text: '用了快两年了，资源更新速度真的快。新出的电视剧当天就能看到，而且从来不卡顿。小草影院就是我的快乐源泉！',
                    rating: 5,
                  },
                  {
                    name: '电影爱好者',
                    text: '片库太全了，从老片到新片都能找到。分类也很清晰，找片特别方便。最重要的是真的免费！希望小草影院越做越好。',
                    rating: 5,
                  },
                  {
                    name: '学生党小李',
                    text: '作为学生预算有限，小草影院完全满足了我的观影需求。高清不卡顿，手机电脑都能看，寝室追剧必备！',
                    rating: 5,
                  },
                  {
                    name: '家庭用户张姐',
                    text: '全家人都在用小草影院，老人看戏曲、孩子看动画片、我们看大片，一个平台全搞定。资源丰富还免费，太实用了！',
                    rating: 5,
                  },
                  {
                    name: '资深剧迷',
                    text: '追了这么多年的剧，小草影院是我用过最稳定的免费平台。播放流畅，更新及时，分类科学，用户体验一级棒！',
                    rating: 5,
                  },
                ].map((review, idx) => (
                  <div
                    key={idx}
                    className="p-5 bg-background-50 border border-background-200/70 rounded-xl"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{review.text}</p>
                    <p className="text-xs font-medium text-foreground-800">—— {review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="px-4 md:px-6 py-8 md:py-12" id="contact">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">如有任何问题或建议，欢迎通过以下方式联系我们</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 mb-12">
                {[
                  {
                    icon: 'ri-mail-line',
                    title: '电子邮箱',
                    value: 'service@xiaocaoyingyuan.com',
                    desc: '24小时内回复',
                  },
                  {
                    icon: 'ri-wechat-line',
                    title: '微信公众号',
                    value: '小草影院',
                    desc: '扫码关注获取最新资讯',
                  },
                  {
                    icon: 'ri-customer-service-line',
                    title: '在线反馈',
                    value: '网站意见反馈',
                    desc: '直接在网站提交意见',
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="p-5 bg-background-50 border border-background-200/70 rounded-xl text-center"
                  >
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h3>
                    <p className="text-sm text-primary-500 font-medium">{item.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100" id="faq">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">关于小草影院的常见疑问解答</p>
              </div>

              <div className="space-y-3 max-w-3xl mx-auto pb-12">
                {faqs.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                      <h3 className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</h3>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}